/**
 * Wallet Callbacks — the webhooks YOUR platform implements and WE call.
 *
 * To run a pack on your platform, your wallet needs to answer five signed
 * requests. We send them as `POST {callback_base_url}<path>` using the default
 * paths `/get_session`, `/get_balance`, `/debit`, `/credit`, `/refund`. Each
 * path is overridable per platform if your wallet uses different routes.
 *
 * EVERY REQUEST IS SIGNED, AND YOU VERIFY IT.
 * We sign the exact JSON bytes we put on the wire. You recompute the signature
 * with your API secret and reject anything that does not match:
 *   signingString = "<unix_ts_seconds>.<HTTP_METHOD_UPPER> <request_path_and_query>.<raw_json_body>"
 *   signature     = "sha256=" + lowercaseHex(HMAC_SHA256(api_secret, signingString))
 * We send two headers:
 *   X-Repackz-Timestamp: <unix_ts_seconds>
 *   X-Repackz-Signature: <signature>
 * Accept the request only when the recomputed signature matches and the
 * timestamp is within ±5 minutes of your clock. Use a constant-time compare.
 *
 * WHAT YOU RETURN.
 * Reply with a success body — either { status: 0, balance, ... } or
 * { error: 0, balance, ... } (both 0 mean success). Money values are always
 * integer minor units (cents). On failure, return a non-zero status/error
 * together with an error_code (and optional error_id / error_message).
 *
 * IDEMPOTENCY.
 * Money-moving callbacks carry a stable transaction_id that is the same on
 * every retry for a given purchase round or card, so you can safely de-duplicate.
 * If you have already processed a transaction_id, reply with the code
 * DUPLICATE_TRANSACTION_ID (and the current balance) — we treat that as success
 * and never double-charge or double-pay.
 *
 * The x-codeSamples on each callback show the exact signed request we send, so
 * you can reproduce our signing string and confirm your verification is correct.
 *
 * Modelled as OpenAPI 3.1 webhooks. Each value is a path item.
 */

const SUCCESS_ENVELOPE_NOTE =
  'Reply with a success body: either `{ status: 0, balance, ... }` or `{ error: 0, balance, ... }` ' +
  '(both `0` mean success). `balance` is required on `get_balance` and optional on every other callback — ' +
  'the balance shown to the player always comes from `get_balance`. All money values are integer minor units (cents).';

const ERROR_ENVELOPE_DESCRIPTION =
  'The wallet rejected the request, or signature/timestamp verification failed on your side. ' +
  'Return a non-zero `status`/`error` with an `error_code` (and optional `error_id`/`error_message`). ' +
  'Either the `{ status: 1, error_code, error_message }` or the ' +
  '`{ error: 1, error_detail: { id, code, message } }` shape is accepted. `status`/`error` is always a number ' +
  '(`0` or `1`); the error code (e.g. `FAILED_TRANS`) goes in `error_code` / `error_detail.code`, never in ' +
  '`status`. We read the outcome from the body, so always set `status`/`error` correctly, whatever HTTP status ' +
  'you return.';

const DUPLICATE_NOTE =
  'Return `DUPLICATE_TRANSACTION_ID` for an already-processed transaction — we treat it as success. Either ' +
  'envelope works; put the current `balance` at the top level, e.g. ' +
  '`{ error: 1, error_detail: { id: 2041, code: "DUPLICATE_TRANSACTION_ID", message }, balance }`.';

const GAME_TYPES = ['loot_pack', 'custom_pack'];

const GAME_TYPE_DESCRIPTION =
  'Game format of the round: `loot_pack` for a regular pack, `custom_pack` for Custom Pack Lab rounds, where ' +
  'the player picks the cards and sets their chances.';

// The promotion block of the shipping callbacks: `promo` on a `shipping_debit` and `data.promo` on the shipping
// events share these keys, the amounts in integer cents.
const PROMO_PROPERTIES = {
  id: { type: 'integer', description: 'The promotion id.', example: 12 },
  label: { type: 'string', description: 'The promotion name.', example: 'November shipping promo' },
  covers: {
    type: 'array',
    items: { type: 'string', enum: ['shipping', 'insurance', 'signature', 'handling'] },
    description: 'The parts of the price the promotion covered. A part it did not cover was paid at its regular price.',
    example: ['shipping', 'handling'],
  },
  regular_amount: {
    type: 'integer',
    description:
      'What the delivery would have cost without the promotion — the regular shipping + handling, chosen add-ons included, in cents.',
    example: 1292,
  },
  discount: {
    type: 'integer',
    description: 'What the promotion took off the regular shipping + handling, in cents — the amount you fund.',
    example: 797,
  },
};

const PROMO_EXAMPLE = { id: 12, label: 'November shipping promo', covers: ['shipping', 'handling'], regular_amount: 1292, discount: 797 };

/** Build the three runnable, self-contained signed-request samples for a callback. */
function callbackSamples(callbackPath: string, bodyFields: Record<string, unknown>) {
  const rubyBody =
    'JSON.generate(' +
    Object.entries(bodyFields)
      .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
      .join(', ') +
    ')';
  const jsBody = 'JSON.stringify(' + JSON.stringify(bodyFields) + ')';

  return [
    {
      lang: 'ruby',
      label: 'Ruby',
      source: [
        "require 'openssl'; require 'json'; require 'rack'",
        '# secret = the API secret you share with Repackz; use it to VERIFY this request.',
        "secret = ENV['REPACKZ_API_SECRET']",
        `path = '${callbackPath}'`,
        `body = ${rubyBody}`,
        'ts = Time.now.to_i.to_s',
        'signing_string = "#{ts}.POST #{path}.#{body}"',
        "expected = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
        '# The request carries X-Repackz-Timestamp and X-Repackz-Signature; recompute and compare:',
        'received_ts  = ts        # request.headers["X-Repackz-Timestamp"]',
        'received_sig = expected  # request.headers["X-Repackz-Signature"]',
        'fresh = (Time.now.to_i - received_ts.to_i).abs <= 300',
        "raise 'reject' unless fresh && Rack::Utils.secure_compare(expected, received_sig)",
        "puts({ status: 0, balance: 9800 }.to_json)",
      ].join('\n'),
    },
    {
      lang: 'node',
      label: 'Node.js',
      source: [
        "const crypto = require('crypto');",
        '// secret = the API secret you share with Repackz; use it to VERIFY this request.',
        "const secret = process.env.REPACKZ_API_SECRET;",
        `const path = '${callbackPath}';`,
        `const body = ${jsBody};`,
        "const ts = Math.floor(Date.now() / 1000).toString();",
        'const signingString = `${ts}.POST ${path}.${body}`;',
        "const expected = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
        '// The request carries X-Repackz-Timestamp and X-Repackz-Signature; recompute and compare:',
        'const receivedTs = ts;        // req.headers["x-repackz-timestamp"]',
        'const receivedSig = expected; // req.headers["x-repackz-signature"]',
        'const fresh = Math.abs(Date.now() / 1000 - Number(receivedTs)) <= 300;',
        'const ok = fresh && crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(receivedSig));',
        "if (!ok) throw new Error('reject');",
        "res.json({ status: 0, balance: 9800 });",
      ].join('\n'),
    },
    {
      lang: 'shell',
      label: 'cURL',
      source: [
        '# The exact signed request Repackz sends to your wallet. Reproduce it to test verification.',
        'SECRET="$REPACKZ_API_SECRET"',
        `PATH_URI='${callbackPath}'`,
        `BODY='${JSON.stringify(bodyFields)}'`,
        'TS=$(date +%s)',
        'SIGNING_STRING="$TS.POST $PATH_URI.$BODY"',
        'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
        'curl -sS -X POST "https://your-wallet.example.com$PATH_URI" \\',
        '  -H "X-Repackz-Timestamp: $TS" \\',
        '  -H "X-Repackz-Signature: $SIG" \\',
        "  -H 'Content-Type: application/json' \\",
        '  -d "$BODY"',
      ].join('\n'),
    },
  ];
}

/**
 * Verification samples for an EVENT webhook. Two things differ from the wallet
 * callbacks above, and both bite people: the signed path is the path of the URL
 * *you* configured (not one of ours), and the reply is a bare 2xx — there is no
 * `{status: 0}` envelope, because we never read the body.
 */
function eventSamples(eventType: string, body: Record<string, unknown>) {
  const path = '/repackz/events';
  const json = JSON.stringify(body);

  return [
    {
      lang: 'ruby',
      label: 'Ruby',
      source: [
        "require 'openssl'; require 'json'; require 'rack'",
        "secret = ENV['REPACKZ_API_SECRET']",
        '# The path is the path of YOUR endpoint URL, exactly as configured.',
        `path = '${path}'`,
        'raw_body = request.body.read          # the exact bytes, not a re-serialized hash',
        "ts  = request.headers['X-Repackz-Timestamp']",
        "sig = request.headers['X-Repackz-Signature']",
        '',
        'signing_string = "#{ts}.POST #{path}.#{raw_body}"',
        "expected = 'sha256=' + OpenSSL::HMAC.hexdigest('SHA256', secret, signing_string)",
        'fresh = (Time.now.to_i - ts.to_i).abs <= 300',
        "halt 401 unless fresh && Rack::Utils.secure_compare(expected, sig.to_s)",
        '',
        'event = JSON.parse(raw_body)',
        "return 200 if already_handled?(event['event_id'])   # at-least-once: de-duplicate",
        `handle(event)                                       # event['event_type'] == '${eventType}'`,
        '200                                                 # any 2xx. Body ignored.',
      ].join('\n'),
    },
    {
      lang: 'node',
      label: 'Node.js',
      source: [
        "const crypto = require('crypto');",
        '// Mount with a raw body parser — the signature covers the exact bytes.',
        "app.post('/repackz/events', express.raw({ type: 'application/json' }), (req, res) => {",
        '  const secret = process.env.REPACKZ_API_SECRET;',
        `  const path = '${path}';`,
        '  const raw = req.body.toString();',
        "  const ts = req.get('X-Repackz-Timestamp');",
        "  const sig = req.get('X-Repackz-Signature');",
        '',
        '  const signingString = `${ts}.POST ${path}.${raw}`;',
        "  const expected = 'sha256=' + crypto.createHmac('sha256', secret).update(signingString).digest('hex');",
        '  const fresh = Math.abs(Date.now() / 1000 - Number(ts)) <= 300;',
        '  if (!fresh || !crypto.timingSafeEqual(Buffer.from(expected), Buffer.from(sig))) {',
        '    return res.sendStatus(401);',
        '  }',
        '',
        '  const event = JSON.parse(raw);',
        '  if (alreadyHandled(event.event_id)) return res.sendStatus(200); // at-least-once',
        `  handle(event); // event.event_type === '${eventType}'`,
        '  res.sendStatus(200); // any 2xx. Body ignored.',
        '});',
      ].join('\n'),
    },
    {
      lang: 'shell',
      label: 'cURL',
      source: [
        '# What we send. Reproduce it to test your verification locally.',
        'SECRET="$REPACKZ_API_SECRET"',
        `PATH_URI='${path}'`,
        `BODY='${json}'`,
        'TS=$(date +%s)',
        'SIGNING_STRING="$TS.POST $PATH_URI.$BODY"',
        'SIG="sha256=$(printf \'%s\' "$SIGNING_STRING" | openssl dgst -sha256 -hmac "$SECRET" | sed \'s/^.* //\')"',
        'curl -sS -X POST "https://your-backend.example.com$PATH_URI" \\',
        '  -H "X-Repackz-Timestamp: $TS" \\',
        '  -H "X-Repackz-Signature: $SIG" \\',
        `  -H "X-Repackz-Event: ${eventType}" \\`,
        `  -H "X-Repackz-Event-Id: ${String(body.event_id)}" \\`,
        "  -H 'Content-Type: application/json' \\",
        '  -d "$BODY"',
      ].join('\n'),
    },
  ];
}

const SHIPPING_SHIPPED_EXAMPLE = {
  event_id: 'evt_5f2c9a1e-3c8d-4d2f-9a11-7c6b0f2e4a83',
  event_type: 'shipping.shipped',
  occurred_at: '2026-08-10T14:32:05Z',
  operator_code: 'your_platform',
  data: {
    shipping_id: 4321,
    status: 'shipped',
    shipping_type: 'delivery',
    account_id: 'player_88421',
    tracking_number: '9400111899223197428490',
    carrier: 'USPS',
    shipped_at: '2026-08-10T14:32:05Z',
    pool_item_ids: [7001, 7002],
    round_ids: ['rnd_134'],
    promo: null,
  },
};

const SHIPPING_DELIVERED_EXAMPLE = {
  event_id: 'evt_9b1d7c42-6a0e-4f5b-8c3d-2e7f1a9b0c64',
  event_type: 'shipping.delivered',
  occurred_at: '2026-08-13T18:47:12Z',
  operator_code: 'your_platform',
  data: {
    shipping_id: 4321,
    status: 'delivered',
    shipping_type: 'delivery',
    account_id: 'player_88421',
    tracking_number: '9400111899223197428490',
    carrier: 'USPS',
    delivered_at: '2026-08-13T18:47:12Z',
    pool_item_ids: [7001, 7002],
    round_ids: ['rnd_134'],
    promo: null,
  },
};

const SHIPPING_CANCELED_EXAMPLE = {
  event_id: 'evt_c3a8e5d0-1b7f-4e29-a6d4-8f0b2c9e7a15',
  event_type: 'shipping.canceled',
  occurred_at: '2026-08-11T09:12:44Z',
  operator_code: 'your_platform',
  data: {
    shipping_id: 4321,
    status: 'canceled',
    shipping_type: 'delivery',
    account_id: 'player_88421',
    reason: 'requested',
    canceled_at: '2026-08-11T09:12:44Z',
    items: [
      { id: 90111, pool_item_id: 7001, settle_status: 'canceled', is_allow_to_cancel_by_operator: false, buyback_amount_cents: 1799 },
      { id: 90112, pool_item_id: 7002, settle_status: 'canceled', is_allow_to_cancel_by_operator: false, buyback_amount_cents: 1350 },
    ],
    pool_item_ids: [7001, 7002],
    round_ids: ['rnd_134'],
    promo: null,
  },
};

// The body in the order we send it: the delivery's own keys, then `fees`, then `promo` where one applies.
const SHIPPING_DEBIT_BODY = {
  token: 'op-session-abc123',
  account_id: 'player_88421',
  amount: 1292,
  currency: 'USD',
  transaction_id: 'ship_4012',
  shipping_id: 4012,
  rounds: [
    { round_id: 'rnd_134', pool_item_id: 7001, fmv: 1999 },
    { round_id: 'rnd_135', pool_item_id: 7002, fmv: 1500 },
  ],
  note: 'shipping',
  fees: { shipping: 992, handling: 300, sales_tax: 0 },
};

const SHIPPING_DEBIT_PROMO_BODY = {
  ...SHIPPING_DEBIT_BODY,
  amount: 495,
  fees: { shipping: 395, handling: 100, sales_tax: 0 },
  promo: PROMO_EXAMPLE,
};

/** A shipping event's request-body examples: a delivery at the regular price, and the same one priced by a promotion. */
const eventExamples = (event: { data: object }) => ({
  regular: { summary: 'No promotion', value: event },
  promotion: {
    summary: 'Priced by a shipping promotion',
    value: { ...event, data: { ...event.data, promo: PROMO_EXAMPLE } },
  },
});

const EVENT_PROMO_SCHEMA = {
  type: 'object',
  nullable: true,
  description:
    'The shipping promotion that priced this delivery, in cents — the same object the `shipping_debit` callback ' +
    'carries as `promo`. `null` when no promotion applied. A delivery that cost the player nothing sends no ' +
    '`shipping_debit`, but its events still say which promotion priced it.',
  required: ['id', 'label', 'covers', 'regular_amount', 'discount'],
  properties: PROMO_PROPERTIES,
};

export const webhooks: Record<string, unknown> = {
  get_session: {
    post: {
      tags: ['Wallet Callbacks'],
      summary: 'Resolve a player session',
      description: [
        'We call this on your wallet at `POST {callback_base_url}/get_session` when a',
        'player launches a pack, to resolve who they are and their starting wallet balance.',
        '',
        'We send the `token` your platform issued for this player. You look it up and',
        'return the player account id (we keep `account_id` and send it back on every later',
        'debit, credit, and refund), plus optional profile fields and their current wallet',
        'balance in cents.',
        '',
        'This is a read-only lookup — it moves no money and is not retried. ' + SUCCESS_ENVELOPE_NOTE,
      ].join('\n'),
      operationId: 'walletGetSession',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description: 'The payload we send. We sign the raw JSON bytes shown here.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'GetSessionRequest',
              required: ['token'],
              properties: {
                token: {
                  type: 'string',
                  description:
                    'The player session token your platform issued at launch. We echo it back so you can ' +
                    'resolve which account this lookup is for.',
                  example: 'op-session-abc123',
                },
              },
            },
            example: { token: 'op-session-abc123' },
          },
        },
      },
      responses: {
        '200': {
          description: 'Player resolved. We read `account_id` (kept for later transactions) and the wallet balance.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'GetSessionResponse',
                required: ['status', 'account_id'],
                properties: {
                  status: { type: 'integer', enum: [0], description: 'Success flag. `0` means success (or use `error: 0`).' },
                  account_id: {
                    type: 'string',
                    description: 'The player account id on your platform. We reuse this on every later debit/credit/refund.',
                    example: 'player_88421',
                  },
                  username: { type: 'string', nullable: true, description: 'Optional player display name.', example: 'highroller_joe' },
                  country: { type: 'string', nullable: true, description: 'Optional player country code.', example: 'US' },
                  currency: { type: 'string', nullable: true, description: 'Wallet currency (ISO code).', example: 'USD' },
                  balance: { type: 'integer', nullable: true, description: 'Current wallet balance in cents.', example: 12500 },
                },
              },
              example: {
                status: 0,
                account_id: 'player_88421',
                username: 'highroller_joe',
                country: 'US',
                currency: 'USD',
                balance: 12500,
              },
            },
          },
        },
        default: {
          description: ERROR_ENVELOPE_DESCRIPTION + ' Common codes: `INVALID_TOKEN`, `INVALID_PLAYER_ID`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': callbackSamples('/get_session', { token: 'op-session-abc123' }),
    },
  },

  get_balance: {
    post: {
      tags: ['Wallet Callbacks'],
      summary: 'Fetch the player wallet balance',
      description: [
        'We call this on your wallet at `POST {callback_base_url}/get_balance` to show the',
        'player their live wallet balance in their browser while they browse and play.',
        '',
        'We send the player session `token` and the `account_id` you returned from',
        '`get_session`. Return the current balance in cents and the wallet currency.',
        '',
        'This is a read-only lookup — it moves no money and is not retried. ' + SUCCESS_ENVELOPE_NOTE,
      ].join('\n'),
      operationId: 'walletGetBalance',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description: 'The payload we send. We sign the raw JSON bytes shown here.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'GetBalanceRequest',
              required: ['token', 'account_id'],
              properties: {
                token: { type: 'string', description: 'The player session token your platform issued.', example: 'op-session-abc123' },
                account_id: { type: 'string', description: 'The player account id you returned from `get_session`.', example: 'player_88421' },
              },
            },
            example: { token: 'op-session-abc123', account_id: 'player_88421' },
          },
        },
      },
      responses: {
        '200': {
          description: 'Current wallet balance and currency.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'GetBalanceResponse',
                required: ['status', 'balance', 'currency'],
                properties: {
                  status: { type: 'integer', enum: [0], description: 'Success flag. `0` means success (or use `error: 0`).' },
                  balance: { type: 'integer', description: 'Current wallet balance in cents.', example: 9800 },
                  currency: { type: 'string', description: 'Wallet currency (ISO code).', example: 'USD' },
                },
              },
              example: { status: 0, balance: 9800, currency: 'USD' },
            },
          },
        },
        default: {
          description: ERROR_ENVELOPE_DESCRIPTION + ' Common codes: `INVALID_TOKEN`, `INVALID_PLAYER_ID`.',
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': callbackSamples('/get_balance', { token: 'op-session-abc123', account_id: 'player_88421' }),
    },
  },

  debit: {
    post: {
      tags: ['Wallet Callbacks'],
      summary: 'Take funds to buy a pack',
      description: [
        'We call this on your wallet at `POST {callback_base_url}/debit` to take money from',
        'the player wallet so they can buy a pack. This runs inside the purchase request as a',
        '**single attempt** — if it fails, the purchase fails.',
        '',
        'Deduct `amount` (cents) from the player wallet and return their new balance. The',
        '`transaction_id` is stable for this purchase round: if you have already processed it,',
        'reply with the code `DUPLICATE_TRANSACTION_ID` and the current balance — we treat that',
        'as success. If the debit fails, we may later call `refund` to reverse anything that',
        'did go through.',
        '',
        'A `custom_pack` round (see `game_type`) has no fixed pack price: `amount` is the quoted price of the',
        'player’s selection (its expected value divided by the game’s RTP), and the round holds a single card.',
        '',
        SUCCESS_ENVELOPE_NOTE,
      ].join('\n'),
      operationId: 'walletDebit',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description: 'The payload we send. We sign the raw JSON bytes shown here.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'DebitRequest',
              required: [
                'token',
                'account_id',
                'amount',
                'currency',
                'transaction_id',
                'round_id',
                'game_id',
                'game_name',
                'game_type',
                'note',
              ],
              properties: {
                token: { type: 'string', description: 'The player session token your platform issued.', example: 'op-session-abc123' },
                account_id: { type: 'string', description: 'The player account id from `get_session`.', example: 'player_88421' },
                amount: { type: 'integer', description: 'Amount to deduct, in cents.', example: 2500 },
                currency: { type: 'string', description: 'Session currency code (selected at session create).', example: 'USD' },
                transaction_id: {
                  type: 'string',
                  description: 'Idempotency key for this debit. Stable per purchase round — identical on every retry.',
                  example: 'dbt_50317',
                },
                round_id: { type: 'string', description: 'Identifier for this purchase round.', example: 'rnd_50317' },
                game_id: { type: 'integer', description: 'The id of the pack being purchased.', example: 5 },
                game_name: {
                  type: 'string',
                  description:
                    'Human-readable title of the pack — reflects the pack rename set in the console at the time of the callback, so it may differ between callbacks of one round if the pack was renamed in between. Match on `game_id` / `round_id`, not on this label.',
                  example: 'HOF Elite MMA Pack',
                },
                game_type: { type: 'string', enum: GAME_TYPES, description: GAME_TYPE_DESCRIPTION, example: 'loot_pack' },
                note: { type: 'string', enum: ['debit'], description: 'Constant. Always `debit`.', example: 'debit' },
              },
            },
            example: {
              token: 'op-session-abc123',
              account_id: 'player_88421',
              amount: 2500,
              currency: 'USD',
              transaction_id: 'dbt_50317',
              round_id: 'rnd_50317',
              game_id: 5,
              game_name: 'HOF Elite MMA Pack',
              game_type: 'loot_pack',
              note: 'debit',
            },
          },
        },
      },
      responses: {
        '200': {
          description:
            'Funds taken. Return the player post-debit balance. The same body is fine for a duplicate `transaction_id`. ' +
            'Optionally include a `jackpot` object to award one of your active jackpot cards on this purchase (see the ' +
            'Items (S2S) → *List active jackpot cards* endpoint) — we place that card into the pack, replacing one normal pull. ' +
            'A `custom_pack` round never awards a jackpot: the instruction is ignored. ' +
            'API-only integrations do not receive this webhook: they pass the same `jackpot.card_id` instruction on the ' +
            'instant-purchase request instead.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'DebitResponse',
                required: ['status'],
                properties: {
                  status: { type: 'integer', enum: [0], description: 'Success flag. `0` means success (or use `error: 0`).' },
                  balance: { type: 'integer', description: 'Player wallet balance after the debit, in cents. Optional — omit it if you cannot read it; never send 0 in its place.', example: 9800 },
                  transaction_id: { type: 'string', description: 'Echo of the debit `transaction_id`.', example: 'dbt_50317' },
                  currency: { type: 'string', description: 'Wallet currency (ISO code).', example: 'USD' },
                  jackpot: {
                    type: 'object',
                    title: 'DebitJackpot',
                    nullable: true,
                    description:
                      'Include only when this debit awards a jackpot card. Names the specific card to place into the ' +
                      'pack, replacing one normal pull. The card must be one of your active jackpot cards (see ' +
                      'GET /jackpot_cards) — otherwise the award is skipped and a normal card is drawn. Runaway guard: ' +
                      'at most 3000 jackpot awards per operator per calendar day (UTC) — an instruction beyond that is ' +
                      'skipped the same way (the purchase itself always succeeds). Idempotent on retries: a ' +
                      'replayed `transaction_id` resolves to the same jackpot outcome, never a second card. A ' +
                      '`custom_pack` round (see `game_type`) never awards a jackpot: the instruction is ignored and ' +
                      'the player gets the card drawn from their own selection.',
                    properties: {
                      card_id: { type: 'integer', description: 'Id of the active jackpot card to award.', example: 90123 },
                    },
                    required: ['card_id'],
                  },
                },
              },
              example: { status: 0, balance: 9800, transaction_id: 'dbt_50317', currency: 'USD' },
            },
          },
        },
        default: {
          description:
            ERROR_ENVELOPE_DESCRIPTION +
            ' Common codes: `INSUFFICIENT_BALANCE`, `CURRENCY_MISMATCH`, `ZERO_AMOUNT_VALUE`, `NEGATIVE_AMOUNT_VALUE`, ' +
            '`INVALID_GAME_ID`, `FAILED_TRANS`, `INVALID_TOKEN`. ' +
            'For a player spending limit, answer `SPENDING_LIMIT_REACHED` or `SPENDING_LIMIT_REACHED_<PERIOD>` (e.g. `_DAY`, `_WEEK`, `_MONTH`) — ' +
            'the code is passed through to the player UI and your `error_message` is shown to the player (*Handling guide → Spending limit reached*). ' +
            DUPLICATE_NOTE,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': callbackSamples('/debit', {
        token: 'op-session-abc123',
        account_id: 'player_88421',
        amount: 2500,
        currency: 'USD',
        transaction_id: 'dbt_50317',
        round_id: 'rnd_50317',
        game_id: 5,
        game_name: 'HOF Elite MMA Pack',
        game_type: 'loot_pack',
        note: 'debit',
      }),
    },
  },

  shipping_debit: {
    post: {
      tags: ['Wallet Callbacks'],
      summary: 'Charge for a physical delivery',
      description: [
        'We call this on your wallet at `POST {callback_base_url}/shipping-debit` to charge the',
        'player for shipping their items to a physical address. Same shape as `debit`, but on its',
        'own path so you can route / meter shipping charges separately.',
        '',
        'Sent once, immediately, when the player confirms shipping — a **single attempt** inside',
        'the create request. Deduct `amount` (cents = shipping + handling + sales tax, when sales tax is',
        'enabled for your operator) and return',
        'the new balance. If it fails (e.g. `INSUFFICIENT_BALANCE`), the delivery is NOT created.',
        'The `transaction_id` is stable per shipment (`ship_<id>`): a repeat replies with',
        '`DUPLICATE_TRANSACTION_ID` + the current balance, which we treat as success.',
        '',
        'While a shipping promotion is active, `amount` is the promotional price the player owes, not the',
        'regular fees. A delivery that costs the player nothing sends no `shipping_debit` at all, so you never',
        'receive a zero-amount `shipping_debit`; the delivery is simply created as paid. `shipping_settle` still',
        'arrives with `amount: 0`, as for every delivery.',
        '',
        '`fees` splits `amount` into its `shipping`, `handling` and `sales_tax` parts: integer cents in the',
        'session currency that add up to `amount` exactly (`shipping` includes any insurance or signature',
        'add-on; `sales_tax` is `0` when sales tax is not enabled for your operator). `promo` is there only',
        'when a shipping promotion applied to the delivery, carrying its `id`, `label` and `covers` (the parts of the',
        'price it covered), the `regular_amount` the player would have paid without it (regular shipping + handling,',
        'chosen add-ons included) and the `discount` you fund:',
        '`discount = regular_amount - (fees.shipping + fees.handling)`. Without a promotion there is no `promo`',
        'key. A **Resend** from the console carries the same JSON fields and values and is signed afresh with',
        'a new timestamp, so always verify the signature against the bytes you receive.',
        '',
        'The `rounds` array maps this single charge to the cards (and their rounds) it covers — ',
        'a single shipment can span multiple rounds. Each entry carries the card `pool_item_id`, ',
        'its `round_id`, and the card `fmv` in cents.',
        '',
        'If a shipped item later turns out undeliverable, that card is bought back via `/credit`',
        '(the standard buyback price, `transaction_id` `crd_<pool_item_id>`, carrying `round_end_state` to',
        'close the round) plus `/refund` for its handling fee and its share of any sales tax — and, only',
        'when the entire shipment is undeliverable, an additional `/refund` for the shipping fee: under a',
        'shipping promotion, the shipping the player paid, including any add-on they paid for, and none when',
        'they paid no shipping. No new callback is added.',
        '',
        SUCCESS_ENVELOPE_NOTE,
      ].join('\n'),
      operationId: 'walletShippingDebit',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description: 'The payload we send. We sign the raw JSON bytes shown here.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'ShippingDebitRequest',
              required: [
                'token',
                'account_id',
                'amount',
                'currency',
                'transaction_id',
                'shipping_id',
                'note',
                'fees',
              ],
              properties: {
                token: { type: 'string', description: 'The player session token your platform issued.', example: 'op-session-abc123' },
                account_id: { type: 'string', description: 'The player account id from `get_session`.', example: 'player_88421' },
                amount: { type: 'integer', description: 'Total shipping charge to deduct, in cents (shipping + handling + sales tax, when enabled for your operator). The promotional price while a shipping promotion is active.', example: 1292 },
                currency: { type: 'string', description: 'Session currency code (selected at session create).', example: 'USD' },
                transaction_id: {
                  type: 'string',
                  description: 'Idempotency key for this shipping charge. Stable per shipment (`ship_<id>`).',
                  example: 'ship_4012',
                },
                shipping_id: { type: 'integer', description: 'The pool_shipping id. A shipping charge is NOT tied to a single game round.', example: 4012 },
                note: { type: 'string', enum: ['shipping'], description: 'Constant. Always `shipping`.', example: 'shipping' },
                rounds: {
                  type: 'array',
                  description:
                    'The cards this charge covers, with their rounds. A single shipment can span multiple rounds.',
                  items: {
                    type: 'object',
                    title: 'ShippingDebitRound',
                    required: ['round_id', 'pool_item_id', 'fmv'],
                    properties: {
                      round_id: { type: 'string', description: 'Identifier for the round this card belongs to.', example: 'rnd_134' },
                      pool_item_id: { type: 'integer', description: 'The card (pool_item) id.', example: 7001 },
                      fmv: { type: 'integer', description: 'Fair market value of the card, in cents.', example: 1999 },
                    },
                  },
                },
                fees: {
                  type: 'object',
                  title: 'ShippingDebitFees',
                  required: ['shipping', 'handling', 'sales_tax'],
                  description:
                    'How `amount` splits, in integer cents in the session currency; the three parts add up to `amount` exactly. On every debit.',
                  properties: {
                    shipping: {
                      type: 'integer',
                      description: 'The shipping charge, including any insurance or signature add-on of the chosen option.',
                      example: 992,
                    },
                    handling: { type: 'integer', description: 'The handling charge.', example: 300 },
                    sales_tax: {
                      type: 'integer',
                      description: 'Sales tax; `0` when sales tax is not enabled for your operator.',
                      example: 0,
                    },
                  },
                },
                promo: {
                  type: 'object',
                  title: 'ShippingDebitPromo',
                  required: ['id', 'label', 'covers', 'regular_amount', 'discount'],
                  description:
                    'Present only when a shipping promotion priced this delivery; the key is left out otherwise, never sent as `null`. ' +
                    'All amounts are integer cents in the session currency, and `discount = regular_amount - (fees.shipping + fees.handling)`.',
                  properties: PROMO_PROPERTIES,
                },
              },
            },
            examples: {
              regular: { summary: 'No promotion', value: SHIPPING_DEBIT_BODY },
              promotion: { summary: 'Priced by a $1 shipping + handling promotion', value: SHIPPING_DEBIT_PROMO_BODY },
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'Charge taken. Return the player post-charge balance. The same body is fine for a duplicate `transaction_id`.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'ShippingDebitResponse',
                required: ['status'],
                properties: {
                  status: { type: 'integer', enum: [0], description: 'Success flag. `0` means success (or use `error: 0`).' },
                  balance: { type: 'integer', description: 'Player wallet balance after the charge, in cents. Optional — omit it if you cannot read it; never send 0 in its place.', example: 8508 },
                  transaction_id: { type: 'string', description: 'Echo of the shipping `transaction_id`.', example: 'ship_4012' },
                  currency: { type: 'string', description: 'Wallet currency (ISO code).', example: 'USD' },
                },
              },
              example: { status: 0, balance: 8508, transaction_id: 'ship_4012', currency: 'USD' },
            },
          },
        },
        default: {
          description:
            ERROR_ENVELOPE_DESCRIPTION +
            ' Common codes: `INSUFFICIENT_BALANCE`, `CURRENCY_MISMATCH`, `ZERO_AMOUNT_VALUE`, `NEGATIVE_AMOUNT_VALUE`, ' +
            '`FAILED_TRANS`, `INVALID_TOKEN`. ' +
            DUPLICATE_NOTE,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': callbackSamples('/shipping-debit', SHIPPING_DEBIT_BODY),
    },
  },

  shipping_settle: {
    post: {
      tags: ['Wallet Callbacks'],
      summary: 'Close a round once its shipped items are fulfilled',
      description: [
        'Closes a round once the warehouse has fulfilled the round\'s shipped items. Sent exactly',
        'once per round (not per item), only when shipping closes the round, so `amount` is always',
        '`0` and `is_round_final` is always `true`. `transaction_id` is `shipclose_<round_id>` and',
        'the call is idempotent. Fires later than `shipping-debit` (when staff mark items shipped)',
        'and is retried.',
        '',
        'No shipping-settle is sent for an unavailable card. If a shipped item turns out',
        'undeliverable, the operator instead receives `/credit` for the standard buyback price',
        '(`transaction_id` `crd_<pool_item_id>`, carrying `round_end_state` to close the round) plus',
        '`/refund` for that card\'s handling fee and its share of any sales tax — and, only when the',
        'entire shipment is undeliverable, an additional `/refund` for the shipping fee.',
        '',
        'We call this on your wallet at `POST {callback_base_url}/shipping-settle`. Make it',
        'idempotent: a repeat of the same `transaction_id` replies with `DUPLICATE_TRANSACTION_ID`',
        '+ the current balance, which we treat as success.',
        '',
        SUCCESS_ENVELOPE_NOTE,
      ].join('\n'),
      operationId: 'walletShippingSettle',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description: 'The payload we send. We sign the raw JSON bytes shown here.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'ShippingSettleRequest',
              required: [
                'token',
                'account_id',
                'amount',
                'currency',
                'transaction_id',
                'round_id',
                'pool_item_ids',
                'status',
                'is_round_final',
                'note',
              ],
              properties: {
                token: { type: 'string', description: 'The player session token your platform issued.', example: 'op-session-abc123' },
                account_id: { type: 'string', description: 'The player account id from `get_session`.', example: 'player_88421' },
                amount: { type: 'integer', enum: [0], description: 'Always `0` — closing a round moves no money.', example: 0 },
                currency: { type: 'string', description: 'Session currency code (selected at session create).', example: 'USD' },
                transaction_id: {
                  type: 'string',
                  description: 'Idempotency key for this settle. Stable per round (`shipclose_<round_id>`).',
                  example: 'shipclose_rnd_134',
                },
                round_id: { type: 'string', description: 'Identifier for the round being closed.', example: 'rnd_134' },
                pool_item_ids: {
                  type: 'array',
                  description: "The round's physically shipped card (pool_item) ids.",
                  items: { type: 'integer', example: 7001 },
                  example: [7001, 7002],
                },
                status: {
                  type: 'string',
                  enum: ['shipped'],
                  description: 'Fulfillment outcome. Currently always `shipped`.',
                  example: 'shipped',
                },
                is_round_final: {
                  type: 'boolean',
                  enum: [true],
                  description: 'Always `true` — this callback fires only when shipping closes the round.',
                  example: true,
                },
                note: { type: 'string', enum: ['shipping_settle'], description: 'Constant. Always `shipping_settle`.', example: 'shipping_settle' },
              },
            },
            example: {
              token: 'op-session-abc123',
              account_id: 'player_88421',
              amount: 0,
              currency: 'USD',
              transaction_id: 'shipclose_rnd_134',
              round_id: 'rnd_134',
              pool_item_ids: [7001, 7002],
              status: 'shipped',
              is_round_final: true,
              note: 'shipping_settle',
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'Round closed. Return the current balance (unchanged). The same body is fine for a duplicate `transaction_id`.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'ShippingSettleResponse',
                required: ['status'],
                properties: {
                  status: { type: 'integer', enum: [0], description: 'Success flag. `0` means success (or use `error: 0`).' },
                  balance: { type: 'integer', description: 'Current wallet balance in cents (unchanged — this moves no money). Optional — you can skip the wallet read and omit it.', example: 488708 },
                  transaction_id: { type: 'string', description: 'Echo of the settle `transaction_id`.', example: 'shipclose_rnd_134' },
                  currency: { type: 'string', description: 'Wallet currency (ISO code).', example: 'USD' },
                },
              },
              example: { status: 0, balance: 488708, transaction_id: 'shipclose_rnd_134', currency: 'USD' },
            },
          },
        },
        default: {
          description:
            ERROR_ENVELOPE_DESCRIPTION +
            ' Common codes: `FAILED_TRANS`, `INVALID_TOKEN`. ' +
            DUPLICATE_NOTE,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': callbackSamples('/shipping-settle', {
        token: 'op-session-abc123',
        account_id: 'player_88421',
        amount: 0,
        currency: 'USD',
        transaction_id: 'shipclose_rnd_134',
        round_id: 'rnd_134',
        pool_item_ids: [7001, 7002],
        status: 'shipped',
        is_round_final: true,
        note: 'shipping_settle',
      }),
    },
  },

  credit: {
    post: {
      tags: ['Wallet Callbacks'],
      summary: 'Pay funds after a sell-back',
      description: [
        'We call this on your wallet at `POST {callback_base_url}/credit` to return money to',
        'the player after they sell a card back (buyback). This is **retried** until it',
        'succeeds or fails with a permanent code, so make it idempotent.',
        '',
        'Add `amount` (cents) to the player wallet and return their new balance. The',
        '`transaction_id` is stable per card: if you have already credited it, reply with the',
        'code `DUPLICATE_TRANSACTION_ID` and the current balance — we treat that as success and',
        'never double-pay.',
        '',
        'A round is one purchase (`round_id` = `rnd_<order_id>`) and each card bought back is its own',
        'credit (`transaction_id` = `crd_<card_id>`), so one `round_id` can produce several credits.',
        '`round_end_state` is `true` only on the credit that closes the round: no card of that purchase',
        'is left with the player, every other credit of the round has already been delivered, and no',
        'shipment of its cards is still pending. Every other credit carries `false`, including one',
        'delivered after the round was already closed. Close the round only on `round_end_state: true`.',
        '',
        'If our records show the round as closed but the flag never reached you, we re-send the last',
        'delivered credit — same `transaction_id`, same `amount` — with `round_end_state: true`. Reply',
        '`DUPLICATE_TRANSACTION_ID` as usual (no second payout), but apply `round_end_state` from that request.',
        '',
        'The currency is that of the session the transaction is attributed to; for cross-session flows ' +
          '(bulk sell, shipping refunds) it can differ from the original debit\'s currency.',
        '',
        SUCCESS_ENVELOPE_NOTE,
      ].join('\n'),
      operationId: 'walletCredit',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description: 'The payload we send. We sign the raw JSON bytes shown here.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'CreditRequest',
              required: [
                'token',
                'account_id',
                'amount',
                'currency',
                'transaction_id',
                'round_id',
                'game_id',
                'game_name',
                'game_type',
                'round_end_state',
                'reason',
                'note',
              ],
              properties: {
                token: { type: 'string', description: 'The player session token your platform issued.', example: 'op-session-abc123' },
                account_id: { type: 'string', description: 'The player account id from `get_session`.', example: 'player_88421' },
                amount: { type: 'integer', description: 'Amount to credit, in cents.', example: 1400 },
                currency: { type: 'string', description: 'Session currency code (selected at session create).', example: 'USD' },
                transaction_id: {
                  type: 'string',
                  description: 'Idempotency key for this credit. Stable per card — identical on every retry.',
                  example: 'crd_77310',
                },
                round_id: { type: 'string', description: 'Identifier for the round this card belongs to.', example: 'rnd_50317' },
                game_id: { type: 'integer', nullable: true, description: 'The id of the pack the card came from, if known.', example: 5 },
                game_name: {
                  type: 'string',
                  nullable: true,
                  description:
                    'Human-readable pack title, if known — reflects the pack rename set in the console at the time of the callback, so it may differ from the debit of the same round if the pack was renamed in between. Match on `game_id` / `round_id`, not on this label.',
                  example: 'HOF Elite MMA Pack',
                },
                game_type: { type: 'string', enum: GAME_TYPES, description: GAME_TYPE_DESCRIPTION, example: 'loot_pack' },
                round_end_state: {
                  type: 'boolean',
                  description:
                    '`true` only on the credit that closes the round (see above); `false` on every other credit of the same `round_id`.',
                  example: true,
                },
                reason: {
                  type: 'string',
                  enum: ['sale', 'auto_sale', 'unavailable_item', 'shipping_canceled'],
                  description: 'Why we are paying the player: `sale` (they sold a card), `auto_sale` (sold automatically after the hold window), `unavailable_item` (warehouse compensation for a card we cannot ship), `shipping_canceled` (they canceled a paid delivery and the card went back to the pool).',
                  example: 'sale',
                },
                note: { type: 'string', enum: ['credit'], description: 'Constant. Always `credit`.', example: 'credit' },
              },
            },
            example: {
              token: 'op-session-abc123',
              account_id: 'player_88421',
              amount: 1400,
              currency: 'USD',
              transaction_id: 'crd_77310',
              round_id: 'rnd_50317',
              game_id: 5,
              game_name: 'HOF Elite MMA Pack',
              game_type: 'loot_pack',
              round_end_state: true,
              reason: 'sale',
              note: 'credit',
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'Funds credited. Return the player post-credit balance. The same body is fine for a duplicate `transaction_id`.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'CreditResponse',
                required: ['status'],
                properties: {
                  status: { type: 'integer', enum: [0], description: 'Success flag. `0` means success (or use `error: 0`).' },
                  balance: { type: 'integer', description: 'Player wallet balance after the credit, in cents. Optional — omit it if you cannot read it; never send 0 in its place.', example: 11200 },
                  transaction_id: { type: 'string', description: 'Echo of the credit `transaction_id`.', example: 'crd_77310' },
                  currency: { type: 'string', description: 'Wallet currency (ISO code).', example: 'USD' },
                },
              },
              example: { status: 0, balance: 11200, transaction_id: 'crd_77310', currency: 'USD' },
            },
          },
        },
        default: {
          description:
            ERROR_ENVELOPE_DESCRIPTION +
            ' Common codes: `CURRENCY_MISMATCH`, `ZERO_AMOUNT_VALUE`, `NEGATIVE_AMOUNT_VALUE`, `INVALID_GAME_ID`, ' +
            '`FAILED_TRANS`, `INVALID_TOKEN`, `UNSUPPORTED_TRANSACTION` (you do not settle this payout type — we treat it as permanent and stop retrying). ' +
            DUPLICATE_NOTE,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': callbackSamples('/credit', {
        token: 'op-session-abc123',
        account_id: 'player_88421',
        amount: 1400,
        currency: 'USD',
        transaction_id: 'crd_77310',
        round_id: 'rnd_50317',
        game_id: 5,
        game_name: 'HOF Elite MMA Pack',
        game_type: 'loot_pack',
        round_end_state: true,
        reason: 'sale',
        note: 'credit',
      }),
    },
  },

  refund: {
    post: {
      tags: ['Wallet Callbacks'],
      summary: 'Reverse a debit when a round fails',
      description: [
        'We call this on your wallet at `POST {callback_base_url}/refund` to put money back',
        'when a purchase round could not complete. This is **retried** until it succeeds or',
        'fails with a permanent code, so make it idempotent.',
        '',
        'Add `amount` (cents) back to the player wallet and return their new balance. We send',
        '`original_transaction_id`, the `transaction_id` of the debit being reversed, so you',
        'can match it up. The refund own `transaction_id` is stable per round: if you have',
        'already processed it, reply with the code `DUPLICATE_TRANSACTION_ID` and the current',
        'balance — we treat that as success and never double-refund.',
        '',
        'The currency is that of the session the transaction is attributed to; for cross-session flows ' +
          '(bulk sell, shipping refunds) it can differ from the original debit\'s currency.',
        '',
        SUCCESS_ENVELOPE_NOTE,
      ].join('\n'),
      operationId: 'walletRefund',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description: 'The payload we send. We sign the raw JSON bytes shown here.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'RefundRequest',
              required: [
                'token',
                'account_id',
                'amount',
                'original_transaction_id',
                'transaction_id',
                'currency',
                'round_id',
                'game_id',
                'game_name',
                'game_type',
                'reason',
                'note',
              ],
              properties: {
                token: { type: 'string', description: 'The player session token your platform issued.', example: 'op-session-abc123' },
                account_id: { type: 'string', description: 'The player account id from `get_session`.', example: 'player_88421' },
                amount: { type: 'integer', description: 'Amount to return, in cents.', example: 2500 },
                original_transaction_id: {
                  type: 'string',
                  description: 'The `transaction_id` of the debit being reversed.',
                  example: 'dbt_50317',
                },
                transaction_id: {
                  type: 'string',
                  description: 'Idempotency key for this refund. Stable per round — identical on every retry.',
                  example: 'rfd_50317',
                },
                currency: { type: 'string', description: 'Session currency code (selected at session create).', example: 'USD' },
                round_id: { type: 'string', description: 'Identifier for the round being reversed.', example: 'rnd_50317' },
                game_id: { type: 'integer', nullable: true, description: 'The id of the pack, if known.', example: 5 },
                game_name: {
                  type: 'string',
                  nullable: true,
                  description:
                    'Human-readable pack title, if known — reflects the pack rename set in the console at the time of the callback, so it may differ from the debit of the same round if the pack was renamed in between. Match on `game_id` / `round_id`, not on this label.',
                  example: 'HOF Elite MMA Pack',
                },
                game_type: { type: 'string', enum: GAME_TYPES, description: GAME_TYPE_DESCRIPTION, example: 'loot_pack' },
                reason: {
                  type: 'string',
                  nullable: true,
                  enum: ['unavailable_item', 'shipping_canceled', 'money_back', null],
                  description: 'Why we are returning money: `unavailable_item` (warehouse compensation), `shipping_canceled` (player canceled a paid delivery), `money_back` (compensation for a damaged shipment). `null` when we cannot attribute the refund — purchase rollbacks and refunds created before this field existed.',
                  example: 'unavailable_item',
                },
                note: {
                  type: 'string',
                  description:
                    'Defaults to `refund`. money_back refunds carry a descriptive note instead, e.g. `money_back: damaged item compensation (FMV)`.',
                  example: 'refund',
                },
              },
            },
            example: {
              token: 'op-session-abc123',
              account_id: 'player_88421',
              amount: 2500,
              original_transaction_id: 'dbt_50317',
              transaction_id: 'rfd_50317',
              currency: 'USD',
              round_id: 'rnd_50317',
              game_id: 5,
              game_name: 'HOF Elite MMA Pack',
              game_type: 'loot_pack',
              reason: 'unavailable_item',
              note: 'refund',
            },
          },
        },
      },
      responses: {
        '200': {
          description: 'Funds returned. Return the player post-refund balance. The same body is fine for a duplicate `transaction_id`.',
          content: {
            'application/json': {
              schema: {
                type: 'object',
                title: 'RefundResponse',
                required: ['status'],
                properties: {
                  status: { type: 'integer', enum: [0], description: 'Success flag. `0` means success (or use `error: 0`).' },
                  balance: { type: 'integer', description: 'Player wallet balance after the refund, in cents. Optional — omit it if you cannot read it; never send 0 in its place.', example: 12500 },
                  transaction_id: { type: 'string', description: 'Echo of the refund `transaction_id`.', example: 'rfd_50317' },
                  original_transaction_id: { type: 'string', description: 'Echo of the reversed debit `transaction_id`.', example: 'dbt_50317' },
                  currency: { type: 'string', description: 'Wallet currency (ISO code).', example: 'USD' },
                },
              },
              example: {
                status: 0,
                balance: 12500,
                transaction_id: 'rfd_50317',
                original_transaction_id: 'dbt_50317',
                currency: 'USD',
              },
            },
          },
        },
        default: {
          description:
            ERROR_ENVELOPE_DESCRIPTION +
            ' Common codes: `NOT_FOUND` (original debit not found), `CURRENCY_MISMATCH`, `FAILED_TRANS`, `INVALID_TOKEN`, `UNSUPPORTED_TRANSACTION` (you do not settle this payout type — we treat it as permanent and stop retrying). ' +
            DUPLICATE_NOTE,
          content: { 'application/json': { schema: { $ref: '#/components/schemas/ErrorEnvelope' } } },
        },
      },
      'x-codeSamples': callbackSamples('/refund', {
        token: 'op-session-abc123',
        account_id: 'player_88421',
        amount: 2500,
        original_transaction_id: 'dbt_50317',
        transaction_id: 'rfd_50317',
        currency: 'USD',
        round_id: 'rnd_50317',
        game_id: 5,
        game_name: 'HOF Elite MMA Pack',
        game_type: 'loot_pack',
        reason: 'unavailable_item',
        note: 'refund',
      }),
    },
  },

  // --- Event webhooks: optional, subscribed in the console, money-less. ---
  'shipping.shipped': {
    post: {
      tags: ['Event Webhooks'],
      summary: 'A delivery left the warehouse',
      description: [
        'Sent the moment a player’s physical delivery is dispatched, carrying the tracking number and',
        'carrier so you can show them where their cards are. Unlike `shipping_settle`, which is a',
        'per-round close marker in the money protocol and waits for the whole round to resolve, this',
        'fires per shipment, always.',
        '',
        'To receive it:',
        '',
        '1. Expose an **HTTPS** endpoint that accepts `POST` with a JSON body — any path you like, on',
        '   a publicly resolvable host. No credentials in the URL.',
        '2. In the operator console open **Webhooks → Add an endpoint**, paste that URL and tick',
        '   `shipping.shipped`.',
        '3. **Verify the signature** with your API secret, exactly as for the wallet callbacks — with',
        '   one difference: the signing string uses the path of *your* endpoint URL, because that is',
        '   what we POST to. Reject a timestamp older than 5 minutes. The samples on the right are',
        '   complete handlers you can paste.',
        '4. **Reply with any 2xx**, body or no body. We never read it: there is no `status: 0` and no',
        '   balance to return.',
        '5. Press **Send test event** in the console. It sends a `ping` in this same envelope, so a',
        '   handler that accepts it will accept the real thing.',
        '',
        'Then de-duplicate on `event_id`: delivery is at-least-once and unordered. A shipment rewound',
        'and re-sent under a new tracking number is a NEW event, not a duplicate.',
      ].join('\n'),
      operationId: 'eventShippingShipped',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description:
          'The payload we send, signed as raw JSON bytes. The envelope (`event_id`, `event_type`, ' +
          '`occurred_at`, `operator_code`, `data`) is identical for every event type — only `data` differs.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'ShippingShippedEvent',
              required: ['event_id', 'event_type', 'occurred_at', 'operator_code', 'data'],
              properties: {
                event_id: {
                  type: 'string',
                  description:
                    'Unique per occurrence and stable across retries — de-duplicate on this. Also sent as the `X-Repackz-Event-Id` header.',
                  example: SHIPPING_SHIPPED_EXAMPLE.event_id,
                },
                event_type: {
                  type: 'string',
                  enum: ['shipping.shipped'],
                  description: 'Also sent as the `X-Repackz-Event` header, so you can route before parsing.',
                  example: 'shipping.shipped',
                },
                occurred_at: {
                  type: 'string',
                  format: 'date-time',
                  description: 'When it happened on our side (UTC, ISO 8601) — not when this attempt was made.',
                  example: SHIPPING_SHIPPED_EXAMPLE.occurred_at,
                },
                operator_code: { type: 'string', description: 'Your platform code.', example: 'your_platform' },
                data: {
                  type: 'object',
                  required: ['shipping_id', 'status', 'account_id', 'pool_item_ids', 'promo'],
                  properties: {
                    shipping_id: { type: 'integer', description: 'Our id for this delivery.', example: 4321 },
                    status: { type: 'string', enum: ['shipped'], example: 'shipped' },
                    shipping_type: {
                      type: 'string',
                      enum: ['delivery', 'smc_transfer'],
                      description: '`delivery` is a physical shipment; `smc_transfer` is a transfer to a vault account.',
                      example: 'delivery',
                    },
                    account_id: {
                      type: 'string',
                      nullable: true,
                      description: 'The player account id from `get_session` — your id for them, not ours.',
                      example: 'player_88421',
                    },
                    tracking_number: {
                      type: 'string',
                      nullable: true,
                      description: 'Carrier tracking number. Null for a shipment sent without one.',
                      example: SHIPPING_SHIPPED_EXAMPLE.data.tracking_number,
                    },
                    carrier: { type: 'string', nullable: true, description: 'Carrier name.', example: 'USPS' },
                    shipped_at: { type: 'string', format: 'date-time', example: SHIPPING_SHIPPED_EXAMPLE.data.shipped_at },
                    pool_item_ids: {
                      type: 'array',
                      description:
                        'The cards travelling in this shipment. A card reported undeliverable is not here — it was bought back and refunded instead.',
                      items: { type: 'integer' },
                      example: [7001, 7002],
                    },
                    round_ids: {
                      type: 'array',
                      description: 'The rounds those cards came from, so you can tie this back to the purchase.',
                      items: { type: 'string' },
                      example: ['rnd_134'],
                    },
                    promo: EVENT_PROMO_SCHEMA,
                  },
                },
              },
            },
            examples: eventExamples(SHIPPING_SHIPPED_EXAMPLE),
          },
        },
      },
      responses: {
        '200': {
          description:
            'Received. Any 2xx will do, with or without a body — nothing in it is read. Reply 2xx to a repeated `event_id` too.',
        },
        default: {
          description:
            'Anything that is not 2xx counts as a failure and is retried (1m, 5m, 30m, 2h, 12h), then parked for a manual Resend in the console.',
        },
      },
      'x-codeSamples': eventSamples('shipping.shipped', SHIPPING_SHIPPED_EXAMPLE),
    },
  },

  'shipping.delivered': {
    post: {
      tags: ['Event Webhooks'],
      summary: 'A delivery reached the player',
      description: [
        'Sent when a player’s delivery is confirmed delivered — normally from the carrier’s tracking,',
        'occasionally by our fulfilment team — so you can close it on your side. It usually follows',
        '`shipping.shipped` for the same `shipping_id` (a shipment marked delivered directly skips that one),',
        'but delivery is unordered: handle it even if that `shipping.shipped` has not reached you yet. Carrier',
        'updates can lag by several hours, and a parcel the carrier never scans as delivered produces no event.',
        '',
        'Setup and signature verification are identical to `shipping.shipped`. Tick `shipping.delivered` for',
        'your endpoint in **Webhooks** — an endpoint subscribed only to `shipping.shipped` does not receive it.',
        'De-duplicate on `event_id`; a shipment produces at most one `shipping.delivered`.',
      ].join('\n'),
      operationId: 'eventShippingDelivered',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description:
          'The payload we send, signed as raw JSON bytes. The envelope (`event_id`, `event_type`, ' +
          '`occurred_at`, `operator_code`, `data`) is identical for every event type — only `data` differs.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'ShippingDeliveredEvent',
              required: ['event_id', 'event_type', 'occurred_at', 'operator_code', 'data'],
              properties: {
                event_id: {
                  type: 'string',
                  description:
                    'Unique per occurrence and stable across retries — de-duplicate on this. Also sent as the `X-Repackz-Event-Id` header.',
                  example: SHIPPING_DELIVERED_EXAMPLE.event_id,
                },
                event_type: {
                  type: 'string',
                  enum: ['shipping.delivered'],
                  description: 'Also sent as the `X-Repackz-Event` header, so you can route before parsing.',
                  example: 'shipping.delivered',
                },
                occurred_at: {
                  type: 'string',
                  format: 'date-time',
                  description: 'When it happened on our side (UTC, ISO 8601) — not when this attempt was made.',
                  example: SHIPPING_DELIVERED_EXAMPLE.occurred_at,
                },
                operator_code: { type: 'string', description: 'Your platform code.', example: 'your_platform' },
                data: {
                  type: 'object',
                  required: ['shipping_id', 'status', 'account_id', 'pool_item_ids', 'promo'],
                  properties: {
                    shipping_id: {
                      type: 'integer',
                      description: 'Our id for this delivery — the same one `shipping.shipped` carried.',
                      example: 4321,
                    },
                    status: { type: 'string', enum: ['delivered'], example: 'delivered' },
                    shipping_type: {
                      type: 'string',
                      enum: ['delivery', 'smc_transfer'],
                      description: '`delivery` is a physical shipment; `smc_transfer` is a transfer to a vault account.',
                      example: 'delivery',
                    },
                    account_id: {
                      type: 'string',
                      nullable: true,
                      description: 'The player account id from `get_session` — your id for them, not ours.',
                      example: 'player_88421',
                    },
                    tracking_number: {
                      type: 'string',
                      nullable: true,
                      description: 'Carrier tracking number. Null for a shipment sent without one.',
                      example: SHIPPING_DELIVERED_EXAMPLE.data.tracking_number,
                    },
                    carrier: { type: 'string', nullable: true, description: 'Carrier name.', example: 'USPS' },
                    delivered_at: {
                      type: 'string',
                      format: 'date-time',
                      description: 'When the delivery was confirmed to us (UTC). Same value as `occurred_at`.',
                      example: SHIPPING_DELIVERED_EXAMPLE.data.delivered_at,
                    },
                    pool_item_ids: {
                      type: 'array',
                      description:
                        'The cards in this shipment. A card reported undeliverable is not here — it was bought back and refunded instead.',
                      items: { type: 'integer' },
                      example: [7001, 7002],
                    },
                    round_ids: {
                      type: 'array',
                      description: 'The rounds those cards came from, so you can tie this back to the purchase.',
                      items: { type: 'string' },
                      example: ['rnd_134'],
                    },
                    promo: EVENT_PROMO_SCHEMA,
                  },
                },
              },
            },
            examples: eventExamples(SHIPPING_DELIVERED_EXAMPLE),
          },
        },
      },
      responses: {
        '200': {
          description:
            'Received. Any 2xx will do, with or without a body — nothing in it is read. Reply 2xx to a repeated `event_id` too.',
        },
        default: {
          description:
            'Anything that is not 2xx counts as a failure and is retried (1m, 5m, 30m, 2h, 12h), then parked for a manual Resend in the console.',
        },
      },
      'x-codeSamples': eventSamples('shipping.delivered', SHIPPING_DELIVERED_EXAMPLE),
    },
  },

  'shipping.canceled': {
    post: {
      tags: ['Event Webhooks'],
      summary: 'A delivery was canceled',
      description: [
        'Sent when a delivery is canceled, whoever did it: the player or you called it off, no card could be',
        'shipped, or our support canceled it. Some of these already reach your wallet as money — the `credit`',
        'for an unavailable card, the `credit` and `refund` calls after a cancel you request — but a delivery',
        'called off before it was paid moves none, so this event is the one signal that covers every cancel and',
        'you need not poll *Get a delivery* to learn of it.',
        '',
        '`reason` says which it was and `items` says how each card ended: its `settle_status`, and in',
        '`buyback_amount_cents` what the player was credited back for it. `pool_item_ids` lists every card of',
        'the delivery, not only the ones that were still travelling — after a cancel none of them is.',
        '',
        'Setup and signature verification are identical to `shipping.shipped`. Tick `shipping.canceled` for',
        'your endpoint in **Webhooks** — an endpoint subscribed only to other events does not receive it.',
        'De-duplicate on `event_id`; a delivery produces at most one `shipping.canceled`.',
      ].join('\n'),
      operationId: 'eventShippingCanceled',
      security: [{ RepackzTimestamp: [], RepackzSignature: [] }],
      requestBody: {
        required: true,
        description:
          'The payload we send, signed as raw JSON bytes. The envelope (`event_id`, `event_type`, ' +
          '`occurred_at`, `operator_code`, `data`) is identical for every event type — only `data` differs.',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              title: 'ShippingCanceledEvent',
              required: ['event_id', 'event_type', 'occurred_at', 'operator_code', 'data'],
              properties: {
                event_id: {
                  type: 'string',
                  description:
                    'Unique per occurrence and stable across retries — de-duplicate on this. Also sent as the `X-Repackz-Event-Id` header.',
                  example: SHIPPING_CANCELED_EXAMPLE.event_id,
                },
                event_type: {
                  type: 'string',
                  enum: ['shipping.canceled'],
                  description: 'Also sent as the `X-Repackz-Event` header, so you can route before parsing.',
                  example: 'shipping.canceled',
                },
                occurred_at: {
                  type: 'string',
                  format: 'date-time',
                  description: 'When it happened on our side (UTC, ISO 8601) — not when this attempt was made.',
                  example: SHIPPING_CANCELED_EXAMPLE.occurred_at,
                },
                operator_code: { type: 'string', description: 'Your platform code.', example: 'your_platform' },
                data: {
                  type: 'object',
                  required: [
                    'shipping_id',
                    'status',
                    'account_id',
                    'reason',
                    'canceled_at',
                    'items',
                    'pool_item_ids',
                    'promo',
                  ],
                  properties: {
                    shipping_id: {
                      type: 'integer',
                      description: 'Our id for this delivery — the same one `shipping.shipped` carries.',
                      example: 4321,
                    },
                    status: { type: 'string', enum: ['canceled'], example: 'canceled' },
                    shipping_type: {
                      type: 'string',
                      enum: ['delivery', 'smc_transfer'],
                      description: '`delivery` is a physical delivery to the player; `smc_transfer` is a transfer to a vault account.',
                      example: 'delivery',
                    },
                    account_id: {
                      type: 'string',
                      nullable: true,
                      description: 'The player account id from `get_session` — your id for them, not ours.',
                      example: 'player_88421',
                    },
                    reason: {
                      type: 'string',
                      enum: ['unavailable', 'requested', 'before_payment', 'other'],
                      description:
                        'Why it ended. `unavailable`: no card could be shipped — each came with a `credit` ' +
                        '(`reason: unavailable_item`). `requested`: it was called off after it was paid, by the player or by ' +
                        'you (*Cancel a delivery*), and the cards were bought back. `before_payment`: it was called off ' +
                        'before it was paid, so nothing was charged. `other`: our support canceled a paid delivery.',
                      example: 'requested',
                    },
                    canceled_at: {
                      type: 'string',
                      format: 'date-time',
                      description: 'When the delivery was canceled (UTC). Same value as `occurred_at`.',
                      example: SHIPPING_CANCELED_EXAMPLE.data.canceled_at,
                    },
                    items: {
                      type: 'array',
                      description: 'Every card of the delivery and how it ended, one object per card.',
                      items: {
                        type: 'object',
                        title: 'ShippingCanceledItem',
                        properties: {
                          id: {
                            type: 'integer',
                            description: 'Delivery LINE id — the same card travelling again in a later delivery gets a new line.',
                            example: 90111,
                          },
                          pool_item_id: { type: 'integer', description: 'Card (pool_item) id.', example: 7001 },
                          settle_status: {
                            type: 'string',
                            enum: ['pending', 'shipped', 'unavailable', 'canceled'],
                            description:
                              '`pending`: not yet resolved. `shipped`: physically fulfilled. `unavailable`: could not be delivered — ' +
                              'bought back and refunded. `canceled`: the delivery was canceled — the card was bought back at the ' +
                              'regular buyback price.',
                            example: 'canceled',
                          },
                          is_allow_to_cancel_by_operator: {
                            type: 'boolean',
                            description: 'Whether this card can still be reported as unfulfillable.',
                            example: false,
                          },
                          buyback_amount_cents: {
                            type: 'integer',
                            nullable: true,
                            description:
                              'Amount (cents, your settlement currency) credited back to the player for this card — the cancel ' +
                              'buyback or the unavailable compensation. `null` while the line is unresolved. API-only operators: ' +
                              'for a `canceled` line no `/credit` is sent, so use this field to re-credit the player yourself. For ' +
                              'an `unavailable` line you still receive `/credit` (`reason: unavailable_item`) — do not also credit ' +
                              'from this field, or the player is paid twice.',
                            example: 1799,
                          },
                        },
                      },
                    },
                    pool_item_ids: {
                      type: 'array',
                      description:
                        'Every card of the delivery — not only the ones that were still travelling, since after a cancel none of ' +
                        'them is. `items` says how each one ended.',
                      items: { type: 'integer' },
                      example: [7001, 7002],
                    },
                    round_ids: {
                      type: 'array',
                      description: 'The rounds those cards came from, so you can tie this back to the purchase.',
                      items: { type: 'string' },
                      example: ['rnd_134'],
                    },
                    promo: EVENT_PROMO_SCHEMA,
                  },
                },
              },
            },
            examples: eventExamples(SHIPPING_CANCELED_EXAMPLE),
          },
        },
      },
      responses: {
        '200': {
          description:
            'Received. Any 2xx will do, with or without a body — nothing in it is read. Reply 2xx to a repeated `event_id` too.',
        },
        default: {
          description:
            'Anything that is not 2xx counts as a failure and is retried (1m, 5m, 30m, 2h, 12h), then parked for a manual Resend in the console.',
        },
      },
      'x-codeSamples': eventSamples('shipping.canceled', SHIPPING_CANCELED_EXAMPLE),
    },
  },
};
